/**
 * Centralized Audit Logging Utility
 * Usage: import { logAudit, AuditLoggers } from '../utils/auditLogger';
 */

const getBaseUrl = () => {
  return (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
};

const getTenantId = () => {
  return localStorage.getItem('tenant_id');
};

const getToken = () => {
  try {
    return localStorage?.getItem('token');
  } catch (e) {
    console.warn('Could not access localStorage for token:', e);
    return null;
  }
};

const getUserName = () => {
  try {
    return localStorage?.getItem('username') || 'Unknown User';
  } catch (e) {
    console.warn('Could not access localStorage for username:', e);
    return 'Unknown User';
  }
};

const formatUserLabel = (userOrId) => {
  if (userOrId && typeof userOrId === 'object') {
    const name = [userOrId.first_name, userOrId.last_name].filter(Boolean).join(' ').trim();
    const email = userOrId.email || userOrId.username;
    if (name && email) return `${name} (${email})`;
    return email || name || userOrId.id || 'Unknown User';
  }
  return userOrId || 'Unknown User';
};

const formatStatusForLog = (value) => {
  if (value === true || value === 1) return 'active';
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'active' || normalized === '1' || normalized === 'true' ? 'active' : 'inactive';
};

/**
 * Log an audit trail entry
 * @param {Object} params - Logging parameters
 * @param {string} params.message - The log message
 * @param {string} params.action - The action performed (e.g., 'Add Fund', 'Update Investor')
 * @param {string} params.functionName - The function name where the action occurred
 * @param {string} params.fileName - The file name where the action occurred
 * @param {string} params.workflowName - The workflow name (e.g., 'Master Data Management')
 * @param {boolean} params.suppressErrors - Whether to suppress errors (default: true)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const logAudit = async ({
  message,
  action,
  functionName,
  fileName,
  workflowName,
  suppressErrors = true,
  details = {}
}) => {
  try {
    let tenantId = getTenantId();
    let token = getToken();
    let userName = getUserName();

    const isLoginEvent = action === 'Login Success' || action === 'Login Failed';

    // Only a Conductor tenant UUID may override. Integration codes like TST_T_103
    // must not be stored as tenant_id — Audit Logs cannot resolve those to a name.
    const tenantOverride = details?.tenantId;
    if (
      tenantOverride &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(tenantOverride))
    ) {
      tenantId = tenantOverride;
    }
    if (details?.username) {
      userName = details.username;
    }
    
    // Only require tenantId and token for non-login events
    if (!isLoginEvent && (!tenantId || !token)) {
      console.warn('Audit logging skipped: Missing tenantId or token');
      return false;
    }

    const baseUrl = getBaseUrl();
    const logUrl = `${baseUrl}/conductor/logs`;

    const logPayload = {
      tenantId,
      userName,
      dateTime: new Date().toISOString(),
      message,
      action,
      functionName,
      fileName,
      workflowName
    };

    const response = await fetch(logUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logPayload),
    });

    if (!response.ok) {
      throw new Error(`Audit log failed: ${response.status} ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error('Error logging audit trail:', error);
    
    if (!suppressErrors) {
      throw error;
    }
    
    return false;
  }
};

/**
 * Log multiple audit entries in batch
 * @param {Array<Object>} logEntries - Array of log entry objects
 * @returns {Promise<Array<boolean>>} - Returns array of success/failure for each entry
 */
export const logAuditBatch = async (logEntries) => {
  const results = await Promise.allSettled(
    logEntries.map(entry => logAudit(entry))
  );
  
  return results.map(result => result.status === 'fulfilled' && result.value);
};

/**
 * Pre-configured loggers for common actions
 */
export const AuditLoggers = {
  /**
   * Master Data loggers
   */
  // Fund related loggers
  fundAdded: (fundName) =>
    logAudit({
      message: `Added new fund: ${fundName}`,
      action: 'Add Fund',
      functionName: 'handleAddFund'
    }),

  fundUpdated: (fundName, fundId) => logAudit({
    message: `Fund '${fundName}' (ID: ${fundId}) was updated`,
    action: 'Update Fund',
    functionName: 'handleSaveEdit'
  }),

  fundImported: (fileName) =>
    logAudit({
      message: `Imported funds from file: ${fileName}`,
      action: 'Import Funds',
      functionName: 'handleImport',
      fileName: fileName || null
    }),

  investorAdded: (investorName) =>
    logAudit({
      message: `Added investor: ${investorName}`,
      action: 'Add Investor',
      functionName: 'handleAddInvestor'
    }),

  investorUpdated: (investorName, investorId) =>
    logAudit({
      message: `Updated investor: ${investorName} (ID: ${investorId})`,
      action: 'Update Investor',
      functionName: 'handleSaveEdit'
    }),

  investorImported: (fileName) =>
    logAudit({
      message: `Imported investors from file: ${fileName}`,
      action: 'Import Investors',
      functionName: 'handleImport',
      fileName: fileName || null
    }),

  /**
   * Document Type loggers
   */
  documentTypeAdded: (docType, fundName) =>
    logAudit({
      message: `Added document type: ${docType} to fund: ${fundName}`,
      action: 'Added Document Type to Extraction Configuration',
      functionName: 'handleAddDocType'
    }),

  documentTypeUpdated: (docType, fundName) =>
    logAudit({
      message: `Updated document type: ${docType} in fund: ${fundName}`,
      action: 'Update Document Type from Extraction Configuration',
      functionName: 'handleEditDocTypeSubmit'
    }),

  documentTypeDeleted: (docType, fundName) =>
    logAudit({
      message: `Deleted document type: ${docType} from fund: ${fundName}`,
      action: 'Delete Document Type from Extraction Configuration',
      functionName: 'handleDeleteDocType'
    }),

  /**
   * Term Management loggers
   */
  termMoved: (oldTerm, newTerm, oldType, newType, fundName) =>
    logAudit({
      message: `Moved term '${oldTerm}' from ${oldType} to '${newTerm}' in ${newType} for fund '${fundName}'`,
      action: `Moved term from ${oldType} to ${newType}`,
      functionName: 'handleEditTerm'
    }),

  termUpdated: (oldTerm, newTerm, termType, fundName) =>
    logAudit({
      message: `Updated ${termType} term from '${oldTerm}' to '${newTerm}' for fund '${fundName}'`,
      action: `Updated ${termType} term for Fund`,
      functionName: 'handleEditTerm'
    }),

  termAdded: (term, termType, fundName) =>
    logAudit({
      message: `Added ${termType} term '${term}' to fund '${fundName}'`,
      action: `Add ${termType === 'include' ? 'Include' : 'Exclude'} Term for Fund`,
      functionName: 'handleAddTerm'
    }),

  // User Role Management loggers
  userRoleAdded: (username, roleName, success = true, errorMessage = '') =>
    logAudit({
      message: success
        ? `Added role '${roleName}' to user ${username}`
        : `Failed to add role '${roleName}' to user ${username}: ${errorMessage}`,
      action: success ? 'User Role Added' : 'User Role Add Failed',
      functionName: 'handleSave'
    }),

  userRightsUpdated: (roleId, roleName, tenantName, rightsCount) =>
    logAudit({
      message: `Updated ${rightsCount} permissions for role '${roleName}' in tenant '${tenantName}'`,
      action: 'User Rights Updated',
      functionName: 'handleSaveAll'
    }),

  userRoleRemoved: (username, roleName, success = true, errorMessage = '') =>
    logAudit({
      message: success
        ? `Removed role '${roleName}' from user ${username}`
        : `Failed to remove role '${roleName}' from user ${username}: ${errorMessage}`,
      action: success ? 'User Role Removed' : 'User Role Remove Failed',
      functionName: 'handleDeleteRole'
    }),

  // User Management loggers
  userCreated: (userEmail, tenantName = null, success = true, errorMessage = '') =>
    logAudit({
      message: success 
        ? `Created new user with email ${userEmail}${tenantName ? ` in tenant ${tenantName}` : ''}` 
        : `Failed to create user with email ${userEmail}${tenantName ? ` in tenant ${tenantName}: ${errorMessage}` : `: ${errorMessage}`}`,
      action: success ? 'User Created' : 'User Creation Failed',
      functionName: success ? 'handleSubmit' : 'handleSaveUser'
    }),

  userUpdated: (userOrId, field, newValue) =>
    logAudit({
      message: `Updated user ${formatUserLabel(userOrId)} (${field} = ${field === 'status' ? formatStatusForLog(newValue) : newValue})`,
      action: 'User Updated',
      functionName: 'saveEditedCell'
    }),

  userDeleted: (userId, userEmail) =>
    logAudit({
      message: `Deleted user ${userEmail || userId || 'email not available'}`,
      action: 'User Deleted',
      functionName: 'handleDeleteUser'
    }),

  userListExported: (userCount, tenantName) =>
    logAudit({
      message: `Exported user list (${userCount} user${userCount === 1 ? '' : 's'})${tenantName ? ` for tenant ${tenantName}` : ''}`,
      action: 'User List Exported',
      functionName: 'handleExportToExcel'
    }),

  userPropertyAdded: (username, propertyName) =>
    logAudit({
      message: `Added property '${propertyName}' to user ${username}`,
      action: 'User Property Added',
      functionName: 'handleSaveProperty'
    }),

  userPropertyRemoved: (username, propertyName) =>
    logAudit({
      message: `Removed property '${propertyName}' from user ${username}`,
      action: 'User Property Removed',
      functionName: 'handleDeleteProperty'
    }),

  passwordResetRequested: (email) =>
    logAudit({
      message: `Password reset email sent to ${email}`,
      action: 'Password Reset Sent',
      functionName: 'handleSubmitResendPasswordReset'
    }),

  passwordChanged: (userOrId) =>
    logAudit({
      message: `Password changed for user ${formatUserLabel(userOrId)}`,
      action: 'Password Changed',
      functionName: 'handleSubmitChangePassword'
    }),

  mfaUpdated: (userId, enabled) =>
    logAudit({
      message: `Updated MFA status for user ${userId} to ${enabled ? 'enabled' : 'disabled'}`,
      action: 'MFA Status Updated',
      functionName: 'handleMfaToggle'
    }),

  // Tenant Role Management loggers
  tenantRoleAdded: (roleName, tenantName, success = true, errorMessage = '') =>
    logAudit({
      message: success 
        ? `Added role '${roleName}' to tenant '${tenantName}'`
        : `Failed to add role '${roleName}' to tenant '${tenantName}': ${errorMessage}`,
      action: success ? 'Tenant Role Added' : 'Tenant Role Add Failed',
      functionName: 'handleAddTenantRole'
    }),

  tenantRoleRemoved: (roleName, tenantName, success = true, errorMessage = '') =>
    logAudit({
      message: success 
        ? `Removed role '${roleName}' from tenant '${tenantName}'`
        : `Failed to remove role '${roleName}' from tenant '${tenantName}': ${errorMessage}`,
      action: success ? 'Tenant Role Removed' : 'Tenant Role Remove Failed',
      functionName: 'handleRemoveTenantRole'
     
    }),

  // Global User Management loggers
  globalUserCreated: (userEmail, tenantName) =>
    logAudit({
      message: `Created new global user with email ${userEmail}${tenantName ? ` for tenant ${tenantName}` : ''}`,
      action: 'Global User Created',
      functionName: 'handleSubmit'
    }),

  globalUserUpdated: (userOrId, field, newValue, tenantName) =>
    logAudit({
      message: `Updated global user ${formatUserLabel(userOrId)} (${field} = ${field === 'status' ? formatStatusForLog(newValue) : newValue})${tenantName ? ` in tenant ${tenantName}` : ''}`,
      action: 'Global User Updated',
      functionName: 'saveEditedCell'
    }),

  globalUserHarmonateStatusUpdated: (userOrId, isHarmonate, tenantName) =>
    logAudit({
      message: `Updated Harmonate status for user ${formatUserLabel(userOrId)} to ${isHarmonate ? 'Harmonate' : 'Non-Harmonate'}${tenantName ? ` in tenant ${tenantName}` : ''}`,
      action: 'Global User Harmonate Status Updated',
      functionName: 'handleHarmonateToggle'
    }),

  globalUserPasswordChanged: (userId, tenantName) =>
    logAudit({
      message: `Password changed for global user ${userId}${tenantName ? ` in tenant ${tenantName}` : ''}`,
      action: 'Global User Password Changed',
      functionName: 'handleSubmitChangePassword'
    }),

  // Generic logger for custom use cases
  custom: (message, action, functionName, details = {}, workflowName) =>
    logAudit({
      message,
      action,
      functionName,
      fileName: typeof details === 'string' ? details : details.fileName || null,
      workflowName,
      details: typeof details === 'object' && !Array.isArray(details) && details !== null 
        ? details 
        : { customData: details }
    }),
    
  // PDF file upload logger
  pdfFileUploaded: (fileName) =>
    logAudit({
      message: `PDF file uploaded: ${fileName}`,
      action: 'PDF File Uploaded',
      functionName: 'handleFileUpload',
      fileName: fileName
    }),
    
  // Excel file upload logger
  excelFileUploaded: (fileName) =>
    logAudit({
      message: `Reference file uploaded: ${fileName}`,
      action: 'Reference File Uploaded',
      functionName: 'handleFileUpload',
      fileName: fileName
    }),
    
  // Document approval logger
  documentApproved: (fileInstanceId, fileName) =>
    logAudit({
      message: `Document ${fileName || fileInstanceId} was approved`,
      action: 'Document Approved',
      functionName: 'handleApprove',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        fileName: fileName || null
      }
    }),

  documentViewed: (fileInstanceId, fileName, fileType = null) => {
    // Skip logging for unclassified files since they're handled by the workflow system
    if (fileType === 'unclassified') {
      return Promise.resolve();
    }
    
    return logAudit({
      message: `Document ${fileName || fileInstanceId} was viewed`,
      action: 'document_viewed',
      functionName: 'handleFileClick',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        actionType: 'view_document',
        fileType
      }
    });
  },

  classificationConfigurationStarted: (fileInstanceId, fileName) => {
    return logAudit({
      message: `Started configuring classification for file: ${fileName || fileInstanceId}`,
      action: 'classification_configuration_started',
      functionName: 'toggleClassificationForm',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        actionType: 'start_classification_configuration'
      }
    });
  },

  /**
   * Log when a user successfully saves a document classification
   * @param {string} fileInstanceId - The ID of the file instance
   * @param {string} fileName - The name of the file
   * @param {Object} classificationData - The classification data that was saved
   */
  classificationConfigurationSaved: (fileInstanceId, fileName, classificationData) => {
    return logAudit({
      message: `Classification saved for file: ${fileName || fileInstanceId}`,
      action: 'classification_saved',
      functionName: 'handleSaveClassification',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        actionType: 'save_classification',
        classificationData: {
          documentTypeId: classificationData.documentType,
          fundId: classificationData.fund,
          fundIdTerm: classificationData.fundIdTerm,
          parserId: classificationData.parserId,
          workflowDefinitionSnowflakeId: classificationData.workflowDefinitionSnowflakeId,
          isMultiInvestor: classificationData.multiInvestor,
          investorId: classificationData.investor,
          investorIdTerm: classificationData.investorIdTerm
        }
      }
    });
  },

  /**
   * Log when a user starts configuring classification for a document
   * @param {string} fileInstanceId - The ID of the file instance
  
// PDF file upload logger
pdfFileUploaded: (fileName) =>
  logAudit({
    message: `PDF file uploaded: ${fileName}`,
    action: 'PDF File Uploaded',
    functionName: 'handleFileUpload',
    fileName: fileName
  }),
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  documentPrintedOrExported: (fileInstanceId, fileName, format) => {
    const isExport = format === 'excel';
    const action = isExport ? 'document_exported' : 'document_printed';
    const formatDisplay = isExport ? 'Excel' : 'PDF';
    
    return logAudit({
      message: `Document ${fileName || fileInstanceId} was ${isExport ? 'exported to Excel' : 'printed'}`,
      action,
      functionName: 'handlePrint',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        actionType: action,
        format: formatDisplay
      }
    });
  },
  
  /**
   * Log when a document is moved to unidentified
   * @param {string} fileInstanceId - The ID of the file instance
   * @param {string} fileName - The name of the file
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  documentMovedToUnidentified(fileInstanceId, fileName) {
    return logAudit({
      message: `Document ${fileName || fileInstanceId} moved to unidentified`,
      action: 'document_moved_to_unidentified',
      functionName: 'moveToUnidentified',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        actionType: 'move_to_unidentified'
      }
    });
  },

  /**
   * Log when a reference file is viewed
   * @param {string} fileInstanceId - The ID of the file instance
   * @param {string} fileName - The name of the file
   * @param {string} integrationId - The integration ID of the file
   * @param {string} workflowId - The workflow ID
   * @param {string} documentTypeId - The document type ID
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  referenceFileViewed(fileInstanceId, fileName, { integrationId, workflowId, documentTypeId }) {
    return logAudit({
      message: `Reference file viewed: ${fileName || fileInstanceId}`,
      action: 'reference file viewed',
      functionName: 'fetchLandingInternalCasRefData',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        integrationId,
        workflowId,
        documentTypeId,
        actionType: 'view_reference_file'
      }
    });
  },

  /**
   * Log when a user views workflow output data
   * @param {string} workflowId - The ID of the workflow
   * @param {string} workflowName - The name of the workflow
   * @param {string} stepId - The ID of the step
   * @param {string} stepName - The name of the step
   * @param {string} viewName - The name of the view being displayed
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  outputDataViewed(workflowId, workflowName, stepId, stepName, viewName) {
    return logAudit({
      message: `Output data viewed for workflow: ${workflowName || workflowId}, step: ${stepName || stepId}`,
      action: 'output data viewed',
      functionName: 'handleFetchOutput',
      fileName: null,
      details: {
        workflowId,
        workflowName,
        stepId,
        stepName,
        viewName,
        actionType: 'view_output_data'
      }
    });
  },
  
  /**
   * Log when a workflow redrive is initiated
   * @param {string} workflowExecutionId - The ID of the workflow execution
   * @param {string} fileName - The name of the file being processed
   * @param {string} fileInstanceId - The ID of the file instance
   * @param {string} workflowName - The name of the workflow
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  workflowRedriveInitiated: (workflowExecutionId, fileName, fileInstanceId, workflowName) => {
    return logAudit({
      message: `File redrive initiated for file: ${fileName || fileInstanceId}`,
      action: 'File redrive initiated',
      functionName: 'handleRedriveConfirm',
      fileName: fileName || null,
     // workflowName: workflowName || 'unclassified',
      details: {
        workflowExecutionId,
        fileInstanceId,
        actionType: 'workflow_redrive'
      }
    });
  },
  
  /**
   * Log when a file deletion is initiated
   * @param {string} fileInstanceId - The ID of the file instance being deleted
   * @param {string} fileName - The name of the file being deleted
   * @param {string} fileType - The type of file being deleted (workflow or reference)
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  fileDeletionInitiated(fileInstanceId, fileName, fileType = 'workflow') {
    const isReference = fileType === 'reference';
    const fileTypeLabel = isReference ? 'Reference file' : 'Workflow file';
    
    return logAudit({
      message: `${fileTypeLabel} deletion initiated: ${fileName || fileInstanceId}`,
      action: isReference ? 'Reference File Deletion Initiated' : 'Workflow File Deletion Initiated',
      functionName: 'handleDeleteConfirm',
      fileName: fileName || null,
      details: {
        fileInstanceId,
        fileType: isReference ? 'reference' : 'workflow',
        workflowName: !isReference && fileType !== 'unclassified' ? fileType : 'unclassified'
      }
    });
  },
  
  /**
   * Log successful user login
   * @param {string} username - The username that logged in
   * @param {string} ipAddress - The IP address of the user
   * @param {Object} userDetails - Additional user details (optional)
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  userLoginSuccess: async (username, ipAddress, userDetails = {}) => {
    try {
      // For login events, we'll use a minimal payload since we might not have full context yet
      const payload = {
        message: `User login successful: ${username}`,
        action: 'Login Success',
        functionName: 'performLogin',
        suppressErrors: true, // Don't fail the login process if logging fails
        details: {
          username,
          ipAddress: ipAddress || 'unknown',
          ...userDetails,
          actionType: 'authentication',
          status: 'success'
        }
      };

      // If we have tenantId and token, include them
      try {
        const tenantId = localStorage?.getItem('tenant_id');
        const token = localStorage?.getItem('token');
        if (tenantId && token) {
          payload.tenantId = tenantId;
          payload.token = token;
        }
      } catch (e) {
        console.warn('Could not access localStorage for audit logging:', e);
      }

      return await logAudit(payload);
    } catch (error) {
      console.error('Failed to log login success:', error);
      return false;
    }
  },

  /**
   * Log user logout
   * @param {string} username - The username that logged out
   * @param {Object} userDetails - Additional user details (optional)
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  userLogout(username, userDetails = {}) {
    return logAudit({
      message: `User logged out: ${username}`,
      action: 'User Logout',
      functionName: 'handleLogout',
     // fileName: 'AuthContext.jsx',
      //workflowName: 'Authentication',
      details: {
        ...userDetails,
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a user switches tenants
   * @param {string} previousTenantId - The ID of the previous tenant
   * @param {string} newTenantId - The ID of the new tenant
   * @param {string} tenantName - The name of the new tenant
   * @param {Object} additionalDetails - Additional details about the tenant switch
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  tenantSwitched(previousTenantId, newTenantId, tenantName, additionalDetails = {}) {
    const previousTenantName = additionalDetails.previousTenantName || 'Previous Tenant';
    const displayTenantName = tenantName && tenantName !== 'Unknown' ? tenantName : 'Unnamed Tenant';
    const sharedDetails = {
      previousTenantId: previousTenantId || 'none',
      previousTenantName,
      newTenantId: newTenantId || 'unknown',
      tenantName: displayTenantName,
      ...additionalDetails,
      timestamp: new Date().toISOString()
    };

    return Promise.all([
      logAudit({
        message: `Tenant switched from ${previousTenantName} to ${displayTenantName}`,
        action: 'Tenant Switched',
        functionName: 'handleTenantChange',
        details: {
          ...sharedDetails,
          tenantId: previousTenantId || getTenantId()
        }
      }),
      logAudit({
        message: `Entered tenant ${displayTenantName} from ${previousTenantName}`,
        action: 'Tenant Entered',
        functionName: 'handleTenantChange',
        details: {
          ...sharedDetails,
          tenantId: newTenantId
        }
      })
    ]).then((results) => results.every(Boolean));
  },

  /**
   * Log failed login attempt
   * @param {string} username - The username that failed to login
   * @param {string} ipAddress - The IP address of the user
   * @param {string} reason - Reason for login failure
   * @param {Object} additionalInfo - Additional error details (optional)
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  userLoginFailed: (username, ipAddress, reason, additionalInfo = {}) => {
    return logAudit({
      message: `Login failed for user: ${username} - ${reason}`,
      action: 'Login Failed',
      functionName: 'performLogin',
      details: {
        username,
        ipAddress,
        reason,
        ...additionalInfo,
        actionType: 'authentication',
        status: 'failed'
      }
    });
  },

  /**
   * Log when a user views all chargebacks
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackViewAll: () => {
    return logAudit({
      message: 'User viewed all chargebacks',
      action: 'View All Chargebacks',
      functionName: 'Chargeback',
      details: {
        actionType: 'view',
        status: 'success',
        module: 'Chargeback',
        viewType: 'list'
      }
    });
  },

  /**
   * Log when a user clicks the Fight Transaction button for a chargeback
   * @param {string} caseNumber - The chargeback case number
   * @param {string} chargebackId - The ID of the chargeback
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackFightTransaction: (caseNumber, chargebackId, additionalDetails = {}) => {
    return logAudit({
      message: `User initiated fight transaction for case: ${caseNumber}`,
      action: 'Fight Transaction Initiated',
      functionName: 'Chargeback',
      details: {
        caseNumber,
        chargebackId,
        ...additionalDetails,
        actionType: 'action',
        status: 'initiated',
        module: 'Chargeback',
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a user downloads a dispute letter PDF
   * @param {string} caseNumber - The chargeback case number
   * @param {string} chargebackId - The ID of the chargeback
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackDownloadPdf: (caseNumber, chargebackId, additionalDetails = {}) => {
    return logAudit({
      message: `User downloaded dispute letter PDF for case: ${caseNumber}`,
      action: 'Dispute Letter PDF Downloaded',
      functionName: 'DisputeLetter',
      details: {
        caseNumber,
        chargebackId,
        ...additionalDetails,
        actionType: 'download',
        status: 'success',
        module: 'Chargeback',
        documentType: 'dispute_letter',
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a user uploads supporting documents for a chargeback
   * @param {string} caseNumber - The chargeback case number
   * @param {string} chargebackId - The ID of the chargeback
   * @param {Array<string>} fileNames - Array of uploaded file names
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackUploadDocument: (caseNumber, chargebackId, fileNames = [], additionalDetails = {}) => {
    const names = (Array.isArray(fileNames) ? fileNames : [fileNames])
      .map((name) => (name == null ? '' : String(name).trim()))
      .filter(Boolean);
    const targets = names.length > 0
      ? names
      : [additionalDetails.fileName].filter(Boolean);

    const writeOne = (name) => logAudit({
      message: name
        ? `User uploaded supporting document: ${name} for case: ${caseNumber}`
        : `User uploaded supporting document(s) for case: ${caseNumber}`,
      action: 'Supporting Documents Uploaded',
      functionName: 'CaseDetails',
      fileName: name || null,
      details: {
        caseNumber,
        chargebackId,
        fileCount: targets.length || fileNames.length,
        fileNames,
        ...additionalDetails,
        fileName: name || additionalDetails.fileName || null,
        actionType: 'upload',
        status: 'success',
        module: 'Chargeback',
        documentType: 'supporting_document',
        timestamp: new Date().toISOString()
      }
    });

    if (targets.length <= 1) {
      return writeOne(targets[0] || null);
    }
    return Promise.all(targets.map((name) => writeOne(name)));
  },

  /**
   * Log when a user downloads an uploaded document
   * @param {string} caseNumber - The chargeback case number
   * @param {string} chargebackId - The ID of the chargeback
   * @param {string} fileKey - The S3 file key/path
   * @param {string} fileName - The name of the file being downloaded
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackDownloadDocument: (caseNumber, chargebackId, fileKey, fileName, additionalDetails = {}) => {
    return logAudit({
      message: `User downloaded document: ${fileName} for case: ${caseNumber}`,
      action: 'Document Downloaded',
      functionName: 'CaseDetails',
      details: {
        caseNumber,
        chargebackId,
        fileKey,
        fileName,
        ...additionalDetails,
        actionType: 'download',
        status: 'success',
        module: 'Chargeback',
        documentType: 'supporting_document',
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a user adds a note to a chargeback
   * @param {string} caseNumber - The chargeback case number
   * @param {string} chargebackId - The ID of the chargeback
   * @param {string} noteContent - The content of the note
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  chargebackAddNote: (caseNumber, chargebackId, noteContent, additionalDetails = {}) => {
    return logAudit({
      message: `User added a note to case: ${caseNumber}`,
      action: 'Note Added',
      functionName: 'CaseDetails',
      details: {
        caseNumber,
        chargebackId,
        noteContent: noteContent.substring(0, 500), // Limit note length for logs
        noteLength: noteContent.length,
        ...additionalDetails,
        actionType: 'note',
        status: 'success',
        module: 'Chargeback',
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a user views the credit card transactions page
   * @param {Object} additionalDetails - Any additional details about the action
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  creditCardViewTransactions: (additionalDetails = {}) => {
    return logAudit({
      message: 'User viewed Credit Card Transactions page',
      action: 'View Credit Card Transactions',
      functionName: 'CreditCardTransactions',
      // fileName: 'CreditCardTransactions.jsx',
      // workflowName: 'Credit Card',
      details: {
        ...additionalDetails,
        actionType: 'view',
        status: 'success',
        module: 'CreditCard',
        timestamp: new Date().toISOString()
      },
      suppressErrors: true
    });
  },

  /**
   * Log when output data is exported
   * @param {string} workflowId - The ID of the workflow
   * @param {string} workflowName - The name of the workflow
   * @param {string} stepId - The ID of the step
   * @param {string} stepName - The name of the step
   * @param {string} viewName - The name of the view being exported
   * @param {string} format - Export format (CSV or Excel)
   * @param {number} rowCount - Number of rows exported
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  outputDataExported: (workflowId, workflowName, stepId, stepName, viewName, format, rowCount) => {
    return logAudit({
      message: `Exported ${rowCount} rows from workflow: ${workflowName || workflowId}, step: ${stepName || stepId} as ${format}`,
      action: 'Output Data Exported',
      functionName: 'handleCustomExport',
      fileName: null,
      details: {
        workflowId,
        workflowName,
        stepId,
        stepName,
        viewName,
        format,
        rowCount,
        actionType: 'export_output_data',
        timestamp: new Date().toISOString()
      },
      suppressErrors: true
    });
  },

  /**
   * Log when a GL Transaction file is uploaded
   * @param {string} fileName - The name of the uploaded file
   * @param {string} sourceGL - The source GL system
   * @param {string} documentType - The document type ID
   * @param {Object} additionalDetails - Additional details about the upload
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glTransactionFileUploaded: (fileName, sourceGL, documentType, additionalDetails = {}) => {
    return logAudit({
      message: `GL Transaction file uploaded: ${fileName} for ${sourceGL} system`,
      action: 'GL Transaction File Uploaded',
      functionName: 'handleFileUpload',
      fileName: fileName || null,
      details: {
        fileName,
        sourceGL,
        documentType,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL Mapping file is uploaded
   * @param {string} fileName - The name of the uploaded file
   * @param {string} glSystem - The GL system (source) being mapped
   * @param {string} documentType - The document type selected
   * @param {Object} additionalDetails - Additional details about the upload
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glMappingFileUploaded: (fileName, glSystem, documentType, additionalDetails = {}) => {
    return logAudit({
      message: `GL Mapping file uploaded: ${fileName} for ${glSystem} system`,
      action: 'GL Mapping File Uploaded',
      functionName: 'handleFileUpload',
      fileName: fileName || null,
      details: {
        fileName,
        glSystem,
        documentType,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL mapping is saved
   * @param {string} sourceSystem - Source GL system
   * @param {string} targetSystem - Target GL system
   * @param {number} mappingCount - Number of mappings saved
   * @param {Object} additionalDetails - Additional details about the mapping
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glMappingSaved: (sourceSystem, targetSystem, mappingCount, additionalDetails = {}) => {
    return logAudit({
      message: `Saved ${mappingCount} GL mappings from ${sourceSystem} to ${targetSystem}`,
      action: 'GL Mapping Saved',
      functionName: 'handleSave',
      details: {
        sourceSystem,
        targetSystem,
        mappingCount,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL Sync comparison is downloaded
   * @param {string} sourceSystem - Source GL system
   * @param {string} targetSystem - Target GL system
   * @param {Object} additionalDetails - Additional details about the download
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glSyncDownloadComparison: (sourceSystem, targetSystem, additionalDetails = {}) => {
    return logAudit({
      message: `Downloaded GL Sync comparison between ${sourceSystem} and ${targetSystem}`,
      action: 'GL Sync Comparison Downloaded',
      functionName: 'handleDownloadComparison',
      details: {
        sourceSystem,
        targetSystem,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when GL Sync output is downloaded
   * @param {string} sourceSystem - Source GL system
   * @param {string} targetSystem - Target GL system
   * @param {Object} additionalDetails - Additional details about the download
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glSyncDownloadOutput: (sourceSystem, targetSystem, additionalDetails = {}) => {
    return logAudit({
      message: `Downloaded GL Sync output from ${sourceSystem} to ${targetSystem}`,
      action: 'GL Sync Output Downloaded',
      functionName: 'handleDownloadOutput',
      details: {
        sourceSystem,
        targetSystem,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL Sync file is deleted
   * @param {string} fileName - Name of the deleted file
   * @param {string} fileId - ID of the deleted file
   * @param {string} glSystem - GL system the file belongs to
   * @param {Object} additionalDetails - Additional details about the deletion
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glSyncFileDeleted: (fileName, fileId, glSystem, additionalDetails = {}) => {
    return logAudit({
      message: `Deleted GL Sync file: ${fileName}`,
      action: 'GL Sync Transaction Deleted',
      functionName: 'handleDeleteUpload',
      details: {
        fileName,
        fileId,
        glSystem,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL Mapping Workflow is deleted
   * @param {string} sourceSystem - Source GL system
   * @param {string} targetSystem - Target GL system
   * @param {string} mappingId - ID of the mapping
   * @param {Object} additionalDetails - Additional details about the deletion
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glMappingWorkflowDeleted: (sourceSystem, targetSystem, mappingId, additionalDetails = {}) => {
    return logAudit({
      message: `Deleted GL Mapping Workflow from ${sourceSystem} to ${targetSystem}`,
      action: 'GL Mapping Workflow Deleted',
      functionName: 'handleDeleteCard',
      details: {
        sourceSystem,
        targetSystem,
        mappingId,
        ...additionalDetails
      }
    });
  },

  /**
   * Log when a GL Sync mapping is deleted
   * @param {string} sourceSystem - Source GL system
   * @param {string} sourceGlCode - Source GL code
   * @param {string} sourceGlDescription - Source GL description
   * @param {string} mappingId - The ID of the mapping being deleted
   * @param {Object} additionalDetails - Additional details about the deletion
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glSyncMappingDeleted: (sourceSystem, sourceGlCode, sourceGlDescription, mappingId, additionalDetails = {}) => {
    return logAudit({
      message: `GL Sync Mapping deleted: ${sourceSystem} (${sourceGlCode})`,
      action: 'GL Sync - Mapping Deleted',
      functionName: 'handleDeleteMapping',
      workflowName: 'GL Sync',
      details: {
        ...additionalDetails,
        sourceSystem,
        sourceGlCode,
        sourceGlDescription,
        mappingId,
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when a GL Sync mapping is created or updated
   * @param {string} sourceSystem - Source GL system
   * @param {string} sourceGlCode - Source GL code
   * @param {string} sourceGlDescription - Source GL description
   * @param {string} targetSystem - Target GL system
   * @param {string} targetGlCode - Target GL code
   * @param {string} targetGlDescription - Target GL description
   * @param {string} mappingId - The ID of the mapping (for updates)
   * @param {Object} additionalDetails - Additional details about the mapping
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glSyncMappingUpdated: (
    sourceSystem,
    sourceGlCode,
    sourceGlDescription,
    targetSystem,
    targetGlCode,
    targetGlDescription,
    mappingId = null,
    additionalDetails = {}
  ) => {
    const isUpdate = !!mappingId;
    return logAudit({
      message: `GL Sync Mapping ${isUpdate ? 'updated' : 'created'}: ${sourceSystem} (${sourceGlCode}) to ${targetSystem} (${targetGlCode})`,
      action: isUpdate ? 'GL Sync - Mapping Updated' : 'GL Sync - Mapping Created',
      functionName: isUpdate ? 'handleDestinationChange' : 'handleUseSuggestedMatch',
      workflowName: 'GL Sync',
      details: {
        ...additionalDetails,
        sourceSystem,
        sourceGlCode,
        sourceGlDescription,
        targetSystem,
        targetGlCode,
        targetGlDescription,
        mappingId,
        timestamp: new Date().toISOString()
      }
    });
  },

  /**
   * Log when GL Accounts are deleted
   * @param {string} glSystem - The GL system from which accounts were deleted
   * @param {number} accountCount - Number of accounts deleted
   * @param {string} accountId - Specific account ID (for single account deletion)
   * @param {Object} additionalDetails - Additional details about the deletion
   * @returns {Promise<boolean>} - Returns true if logging was successful
   */
  glAccountsDeleted: (glSystem, accountCount, accountId = null, additionalDetails = {}) => {
    const isBulkDelete = accountId === null;
    const message = isBulkDelete 
      ? `Deleted all (${accountCount}) accounts from ${glSystem}`
      : `Deleted account ${accountId} from ${glSystem}`;

    return logAudit({
      message,
      action: isBulkDelete ? 'GL Accounts Bulk Deleted' : 'GL Account Deleted',
      functionName: isBulkDelete ? 'confirmDeleteAll' : 'handleDeleteAccount',
      details: {
        glSystem,
        accountCount,
        accountId,
        ...additionalDetails
      }
    });
  },

  /**
 * Log when multiple files are bulk deleted
 * @param {Array<string>} fileIds - Array of file instance IDs being deleted
 * @param {Array<string>} fileNames - Array of file names being deleted
 * @param {string} fileType - Type of files being deleted (workflow or reference)
 * @param {Object} additionalDetails - Additional details about the bulk deletion
 * @returns {Promise<boolean>} - Returns true if logging was successful
 */
bulkFileDeletionInitiated: async (fileIds, fileNames, fileType = 'workflow', additionalDetails = {}) => {
  try {
    const fileCount = fileIds.length;
    const message = `Bulk deleted ${fileCount} ${fileType} file(s): ${fileNames.join(', ').substring(0, 200)}${fileNames.join(', ').length > 200 ? '...' : ''}`;
    
    return logAudit({
      message,
      action: `Bulk Delete ${fileType.charAt(0).toUpperCase() + fileType.slice(1)} Files`,
      functionName: 'handleBulkDelete',
      // fileName: 'WorkflowDrawer.jsx',
      // workflowName: 'Document Management',
      details: {
        fileCount,
        fileIds,
        fileNames,
        fileType,
        ...additionalDetails
      }
    });
  } catch (error) {
    console.error('Error logging bulk file deletion:', error);
    return false;
  }
},

// In auditLogger.js, add this to the AuditLoggers object
glAccountDeletionInitiated: async (glId, glName, additionalDetails = {}) => {
  try {
    return logAudit({
      message: `GL Account deleted: ${glName} (ID: ${glId})`,
      action: 'Delete GL Account',
      functionName: 'confirmDeleteAccount',
      details: {
        glId,
        glName,
        ...additionalDetails
      }
    });
  } catch (error) {
    console.error('Error in glAccountDeletionInitiated:', error);
    return false;
  }
}

};

export default {
  logAudit,
  logAuditBatch,
  AuditLoggers
};
